/**
 * Executes an array of async task factories with a concurrency limit.
 * Unlike Promise.all which fires everything at once, this ensures at most
 * `concurrency` tasks run simultaneously -- preventing upstream 504s when
 * moa-yum-api queues too many requests behind the API Gateway timeout.
 *
 * @param tasks    Array of zero-arg functions that each return a Promise<T>
 * @param concurrency  Max number of tasks running at the same time (default 4)
 * @param onProgress   Optional callback fired after each task settles, receives completed count
 * @returns            Array of results in the same order as `tasks`
 */
export async function promisePool<T>(
  tasks: (() => Promise<T>)[],
  concurrency = 4,
  onProgress?: (completed: number, total: number) => void,
): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let nextIndex = 0;
  let completedCount = 0;

  async function runNext(): Promise<void> {
    while (nextIndex < tasks.length) {
      const idx = nextIndex++;
      results[idx] = await tasks[idx]();
      completedCount++;
      onProgress?.(completedCount, tasks.length);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    () => runNext(),
  );

  await Promise.all(workers);
  return results;
}
